/**
 * FillInBlankTap — Completar huecos con palabras tipo tag (estilo Duolingo)
 * Las palabras se muestran abajo como tags; al pulsar una, anima desde abajo hasta su hueco
 */

import { useState, useMemo, useId, useRef, useEffect } from 'react';
import { motion, LayoutGroup, useReducedMotion } from 'framer-motion';
import { motionPressWhileTap } from '@/constants/motionPress';
import { cn } from '@/utils/cn';
import { shuffle } from '@/utils/shuffle';
import type { FillInBlankTapActivity, FillInBlankTapAnswer } from '@/types/story.types';
import styles from '../StoryPage.module.scss';

const BLANK = '_____';

function makeId() {
  return `tag-${crypto.randomUUID().slice(0, 8)}`;
}

interface TagItem {
  id: string;
  word: string;
}

interface PlacedItem {
  id: string;
  word: string;
}

const FILL_IN_BLANK_TAP_FEEDBACK_DELAY_MS = 500;

interface FillInBlankTapProps {
  activity: FillInBlankTapActivity;
  answer: FillInBlankTapAnswer | undefined;
  onChange: (answers: string[]) => void;
  onAllFilled?: () => void;
  correctness?: boolean[];
  /** Posiciones que vienen ya rellenas y no se pueden mover (en el reintento,
   * los huecos acertados en el primer intento quedan fijados en verde). */
  lockedPositions?: number[];
  disabled?: boolean;
}

export function FillInBlankTap({
  activity,
  answer,
  onChange,
  onAllFilled,
  correctness,
  lockedPositions,
  disabled = false,
}: FillInBlankTapProps) {
  const lockedSet = useMemo(
    () => new Set(lockedPositions ?? []),
    [lockedPositions]
  );
  const reducedMotion = useReducedMotion();
  const tapFeedback = reducedMotion ? undefined : motionPressWhileTap;
  const layoutGroupId = useId();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const parts = useMemo(() => activity.textWithBlanks.split(BLANK), [activity.textWithBlanks]);

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);
  const blankCount = Math.min(parts.length - 1, activity.correctAnswers.length);

  const [placedAnswers, setPlacedAnswers] = useState<PlacedItem[]>(() => {
    const initial = answer?.answers ?? [];
    return initial.length >= blankCount
      ? initial.slice(0, blankCount).map((w) => ({ id: makeId(), word: w }))
      : Array(blankCount).fill(null).map(() => ({ id: '', word: '' }));
  });

  const [availableWords, setAvailableWords] = useState<TagItem[]>(() => {
    const used = answer?.answers?.filter(Boolean) ?? [];
    if (used.length === 0) {
      return shuffle(activity.words).map((w) => ({ id: makeId(), word: w }));
    }
    const pool = [...activity.words];
    for (const w of used) {
      const idx = pool.indexOf(w);
      if (idx >= 0) pool.splice(idx, 1);
    }
    return shuffle(pool).map((w) => ({ id: makeId(), word: w }));
  });

  const handleWordTap = (item: TagItem) => {
    if (disabled) return;
    const firstEmptyIndex = placedAnswers.findIndex((p) => !p.word);
    if (firstEmptyIndex === -1) return;

    const newPlaced = placedAnswers.map((p, i) =>
      i === firstEmptyIndex ? { id: item.id, word: item.word } : p
    );
    setPlacedAnswers(newPlaced);
    setAvailableWords((prev) => prev.filter((t) => t.id !== item.id));
    onChange(newPlaced.map((p) => p.word));

    if (newPlaced.every((p) => p.word) && onAllFilled) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onAllFilled();
        timeoutRef.current = null;
      }, FILL_IN_BLANK_TAP_FEEDBACK_DELAY_MS);
    }
  };

  const handleBlankTap = (index: number) => {
    if (disabled) return;
    if (lockedSet.has(index)) return;
    const item = placedAnswers[index];
    if (!item?.word) return;

    const newPlaced = placedAnswers.map((p, i) => (i === index ? { id: '', word: '' } : p));
    setPlacedAnswers(newPlaced);
    setAvailableWords((prev) => shuffle([...prev, { id: item.id, word: item.word }]));
    onChange(newPlaced.map((p) => p.word));
  };

  return (
    <LayoutGroup id={layoutGroupId}>
      <div className={styles.story__fillInBlankTap}>
        <div className={styles.story__fillInBlankTapText}>
          {parts.map((text, i) => (
            <span key={i}>
              {text}
              {i < blankCount && (
                <motion.button
                  type="button"
                  className={cn(
                    styles.story__fillInBlankTapSlot,
                    placedAnswers[i]?.word && styles['story__fillInBlankTapSlot--filled']
                  )}
                  onClick={() => handleBlankTap(i)}
                  disabled={disabled || !placedAnswers[i]?.word || lockedSet.has(i)}
                  whileTap={tapFeedback}
                  layout
                >
                  {(() => {
                    const item = placedAnswers[i];
                    return item?.word ? (
                      <motion.span
                        layoutId={item.id}
                        className={cn(
                          styles.story__fillInBlankTapTagChip,
                          correctness && correctness[i] === true && styles['story__fillInBlankTapTagChip--correct'],
                          correctness && correctness[i] === false && styles['story__fillInBlankTapTagChip--incorrect'],
                          !correctness && lockedSet.has(i) && styles['story__fillInBlankTapTagChip--correct']
                        )}
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                      >
                        {item.word}
                      </motion.span>
                    ) : (
                    <span className={styles.story__fillInBlankTapPlaceholder}>&nbsp;</span>
                    );
                  })()}
                </motion.button>
              )}
            </span>
          ))}
        </div>

        <div className={styles.story__fillInBlankTapWords}>
          {availableWords.map((item) => (
            <motion.button
              key={item.id}
              type="button"
              className={styles.story__fillInBlankTapTag}
              onClick={() => handleWordTap(item)}
              disabled={disabled}
              whileTap={tapFeedback}
              layout
            >
              <motion.span
                layoutId={item.id}
                className={styles.story__fillInBlankTapTagChip}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
              >
                {item.word}
              </motion.span>
            </motion.button>
          ))}
        </div>
      </div>
    </LayoutGroup>
  );
}
